import {useEffect, useRef} from 'react';
import {
  sendTimeSpentEvent,
  TAB_TIME_SPENT_EVENT_MAP,
} from 'utilities/analytics';

/**
 * Hook to track time spent on a specific tab with Page Visibility API integration.
 * Pauses timer when browser tab is not visible and resumes when visible again.
 * Sends analytics event when tab changes or component unmounts.
 *
 * @param currentTabId - The ID of the currently selected tab
 */
export const useTabTimeTracking = (currentTabId: string) => {
  const startTimeRef = useRef<number>(Date.now());
  const accumulatedTimeRef = useRef<number>(0);
  const isVisibleRef = useRef<boolean>(true);
  const previousTabIdRef = useRef<string>(currentTabId);

  useEffect(() => {
    // Handle visibility change
    const handleVisibilityChange = () => {
      const isVisible = document.visibilityState === 'visible';
      const now = Date.now();

      if (!isVisible && isVisibleRef.current) {
        // Tab became hidden - pause timer by accumulating time
        const elapsed = now - startTimeRef.current;
        accumulatedTimeRef.current += elapsed;
        isVisibleRef.current = false;
      } else if (isVisible && !isVisibleRef.current) {
        // Tab became visible - resume timer
        startTimeRef.current = now;
        isVisibleRef.current = true;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    const previousTabId = previousTabIdRef.current;

    // When tab changes, send event for the previous tab
    if (previousTabId !== currentTabId && previousTabId) {
      const now = Date.now();
      let totalTimeSpent = accumulatedTimeRef.current;

      // Add current session time if visible
      if (isVisibleRef.current) {
        totalTimeSpent += now - startTimeRef.current;
      }

      const timeSpentSeconds = totalTimeSpent / 1000;
      const eventName = TAB_TIME_SPENT_EVENT_MAP[previousTabId];

      if (eventName && timeSpentSeconds > 0) {
        sendTimeSpentEvent(eventName, timeSpentSeconds, 'Manager Assistant');
      }

      // Reset for new tab
      accumulatedTimeRef.current = 0;
      startTimeRef.current = now;
      previousTabIdRef.current = currentTabId;
    }

    // Cleanup on unmount - send final event
    return () => {
      const now = Date.now();
      let totalTimeSpent = accumulatedTimeRef.current;

      // Add current session time if visible
      if (isVisibleRef.current) {
        totalTimeSpent += now - startTimeRef.current;
      }

      const timeSpentSeconds = totalTimeSpent / 1000;
      const eventName = TAB_TIME_SPENT_EVENT_MAP[currentTabId];

      if (eventName && timeSpentSeconds > 0) {
        sendTimeSpentEvent(eventName, timeSpentSeconds, 'Manager Assistant');
      }
    };
  }, [currentTabId]);
};
